import { useEffect, useState } from "react";
import { useParams } from 'react-router';
import { containerMiddlewareAdEPortoEnergy } from "../../../../server/constants.api";
import useFetch from "../../../../server/useFetch";
import { useTranslation } from "react-i18next";
import { Row, Col, Flex } from 'antd';
import { alignKeys } from "../../../../utils/hooks/alignKeys";
import { NewWidget } from "../../Atomics/NewWidget";
import { unitConverterArrays, unitConvertSingleValue } from "../../../../utils/hooks/unitConverter";

export const TimelineLoadDiagram = ({ dataToFilter }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [metricChart, setMetricChart] = useState('Active Power');

  const { data: loadDiagramBigNumbers, isFetching: loadDiagramBigNumbersIsFetching, refetch: refetchLoadDiagramBigNumbers } = useFetch(
    "big-numbers", "getLoadDiagramBigNumbers",
    {
      id: id,
      //Traded because we need the last 31 days
      startDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDay?.toISOString()?.split('.')[0],
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const { data: loadDiagramActivePower, isFetching: loadDiagramActivePowerIsFetching, refetch: refetchLoadDiagramActivePower } = useFetch(
    "active-power", "getLoadDiagramActivePower",
    {
      id: id,
      //Traded because we need the last 31 days
      startDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDay?.toISOString()?.split('.')[0],
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const { data: loadDiagramReactivePower, isFetching: loadDiagramReactivePowerIsFetching, refetch: refetchLoadDiagramReactivePower } = useFetch(
    "reactive-power", "getLoadDiagramReactivePower",
    {
      id: id,
      //Traded because we need the last 31 days
      startDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDay?.toISOString()?.split('.')[0],
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  useEffect(() => {
    refetchLoadDiagramActivePower();
    refetchLoadDiagramReactivePower();
    refetchLoadDiagramBigNumbers();
  }, [dataToFilter]);

  return (
    <Flex id='Timeline Load Diagrams' className='ant-anchors-content-item' gap={32} vertical="true">
      <Row gutter={[16, 16]}>
        <Col span={4}>
          <Flex gap={16} vertical="true">
            <NewWidget data={{
              background: 'white',
              title: t('topEnergy'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramActivePowerIsFetching,
                data: loadDiagramActivePower?.data?.peakActivePower ? { number: loadDiagramActivePower?.data?.peakActivePower, unit: loadDiagramActivePower?.data?.unit, icon: "Bolt" } : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('inductiveConsumption'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramBigNumbersIsFetching,
                data: loadDiagramBigNumbers?.data?.inductiveConsumption ? getBigNumber(loadDiagramBigNumbers?.data?.inductiveConsumption) : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('energyProduced'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramBigNumbersIsFetching,
                data: loadDiagramBigNumbers?.data?.energyProduced ? getBigNumber(loadDiagramBigNumbers?.data?.energyProduced) : null
              }]
            }} />
          </Flex>
        </Col>
        <Col span={4}>
          <Flex gap={16} vertical="true">
            <NewWidget data={{
              background: 'white',
              title: t('activeConsumption'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramBigNumbersIsFetching,
                data: loadDiagramBigNumbers?.data?.activeConsumption ? getBigNumber(loadDiagramBigNumbers?.data?.activeConsumption) : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('capacitiveConsumption'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramBigNumbersIsFetching,
                data: loadDiagramBigNumbers?.data?.capacitiveConsumption ? getBigNumber(loadDiagramBigNumbers?.data?.capacitiveConsumption) : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('gridInjection'),
              charts: [{
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                height: '94px',
                isFetching: loadDiagramBigNumbersIsFetching,
                data: loadDiagramBigNumbers?.data?.gridInjection ? getBigNumber(loadDiagramBigNumbers?.data?.gridInjection) : null
              }]
            }} />
          </Flex>
        </Col>
        <Col span={16}>
          <NewWidget data={{
            background: 'white',
            title: t('loadDiagrams'),
            charts: [{
              chartType: 'mixedLineColumnChartApex',
              chartId: 'mixBarChartLineThree',
              height: '422px',
              isFetching: metricChart === "Active Power" ? loadDiagramActivePowerIsFetching : loadDiagramReactivePowerIsFetching,
              data: metricChart === "Active Power" ? (loadDiagramActivePower?.data ? getDailyLoadDiagram(loadDiagramActivePower?.data, t) : null) :
                metricChart === "Reactive Power" ? (loadDiagramReactivePower?.data ? getDailyReactiveLoadDiagram(loadDiagramReactivePower?.data, t) : null) : null
            }],
            buttons: [
              {
                key: "Active Power",
                title: t("activeEnergyAndProduction"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Active Power",
              },
              {
                key: "Reactive Power",
                title: t("indutiveAndCapacitiveEnergy"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Reactive Power",
              }
            ],
          }} />
        </Col>
      </Row>
    </Flex >
  )
};

const getBigNumber = (data) => {
  const { result, newUnit } = unitConvertSingleValue(data.value, data.unit);
  return { number: result, unit: newUnit, icon: "Bolt" };
};

const getDailyLoadDiagram = (data, t) => {
  const { result, keys } = alignKeys('timestamp', { 'Active Power': data?.activePowerData }, { 'Production Power': data?.productionPowerData });
  return getChartsResult(result, data.unit, t, keys, data.contractedPower, data.maxAllowablePower);
};

const getDailyReactiveLoadDiagram = (data, t) => {
  const { result, keys } = alignKeys('timestamp', { 'Reactive Capacitive Power': data?.rawReactiveCapacitivePower }, { 'Reactive Inductive Power': data?.rawReactiveInductivePower });
  return getChartsResult(result, data.unit, t, keys);
};

const getChartsResult = (alignKeysResult, dataUnit, t, keys, contractedPower, maxAllowablePower) => {
  const series = [];

  alignKeysResult?.map((item, index) => {
    const values = Object.values(item)[0];
    const data = Object.entries(values)?.map(([key, value]) => { return value });

    series.push({
      name: t(Object.keys(item)[0]),//TODO: Add Translations
      data: data
    })
  });

  if (series.length > 0) {
    if (keys && contractedPower) {
      const contractedPowerData = keys?.map((item, index) => { return contractedPower });

      series.push({
        name: t('Contracted Power'),
        data: contractedPowerData
      })
    }

    if (keys && maxAllowablePower) {
      const maxAllowablePowerData = keys?.map((item, index) => { return maxAllowablePower });

      series.push({
        name: t('Max Allowed Power'),
        data: maxAllowablePowerData
      })
    }
  }

  //Convert unit
  const { convertedResult, newUnit } = unitConverterArrays(series?.map(x => x.data.map(y => y)), dataUnit);
  const convertedSeries = series?.map((x, index) => {
    return {
      name: x.name,
      type: "line",
      data: convertedResult[index]
    }
  });

  return {
    "series": convertedSeries,
    "categories": [],
    "labels": keys?.map(x => {
      const date = new Date(Number(x) * 1000);
      const hour = date.getHours();
      const minutes = date.getMinutes();

      // Formata como 'MM-DD', com dois dígitos para mês e dia
      return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }),
    "xAxisType": null,
    "tooltip": null,
    "unit": newUnit,
    "columnWidth": 20,
    "xAxisLabel": null,
    "yAxisLabel": newUnit,
    yAxisLabelRotate: -90,
    "color": null,
    tickAmount: 20,
    "filename": "Gráfico exportado",
    "headerValue": "valores",
    "headerCategory": "categorias",
    "showTooltipTitle": true
  }
};